import { useState, useCallback } from 'react';
import { Box, Typography, styled, Menu, MenuItem } from '@mui/material';
import Dialog, { DialogPrimaryButton } from './Dialog';
import StatusBadge from './StatusBadge';
import api from '../services/api';
import { SERVICE_OPTIONS, SERVICE_STATUS_VARIANTS } from '../constants/service';
import { ReactComponent as TrashIcon } from '../assets/icons/24/remove.svg';

const ServiceRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '12px 0',
  borderBottom: '1px solid #494949',
  '&:last-of-type': {
    borderBottom: 'none',
  },
});

const ServiceLogo = styled(Box)({
  width: 48,
  height: 48,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    width: 48,
    height: 48,
  },
});

const ServiceName = styled(Typography)({
  flex: 1,
  fontSize: 16,
  fontWeight: 500,
  color: '#FFFFFF',
});

const ServiceActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const StatusMenu = styled(Menu)({
  '& .MuiPaper-root': {
    background: '#303030',
    borderRadius: 14,
    marginTop: 4,
    border: '1px solid #494949',
  },
  '& .MuiList-root': {
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    gap: 12,
  },
});

const StatusMenuItem = styled(MenuItem)({
  padding: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const RattachementText = styled(Typography)({
  fontSize: 14,
  fontWeight: 900,
  color: '#B9FF66',
  letterSpacing: 0.1,
  cursor: 'pointer',
});

const TrashButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  color: '#ACACAC',
  '&:hover': { color: '#FFFFFF', opacity: 0.9 },
});

const ApplicationsDialog = ({
  open,
  onClose,
  services = [],
  userId,
  onAttachSuccess,
  onRemoveSuccess,
  onStatusChange,
}) => {
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [statusService, setStatusService] = useState(null);

  const statusMenuOpen = Boolean(statusAnchorEl);

  const handleStatusClick = useCallback((event, service) => {
    setStatusAnchorEl(event.currentTarget);
    setStatusService(service);
  }, []);

  const handleStatusMenuClose = useCallback(() => {
    setStatusAnchorEl(null);
    setStatusService(null);
  }, []);

  const handleRetrait = () => {
    // TODO: Handle withdrawal action
    onClose();
  };

  const handleRemove = useCallback(
    async (serviceId) => {
      if (!serviceId) return;
      const response = await api.delete(`/services/${serviceId}`);
      if (response?.ok) {
        handleStatusMenuClose();
        onRemoveSuccess?.();
      }
    },
    [onRemoveSuccess, handleStatusMenuClose]
  );

  const handleStatusSelect = useCallback(
    async (newStatus) => {
      if (!statusService?.id) return;
      const response = await api.patch(`/services/${statusService.id}/status`, {
        status: newStatus,
      });
      if (response?.ok) {
        handleStatusMenuClose();
        onStatusChange?.();
      }
    },
    [statusService?.id, handleStatusMenuClose, onStatusChange]
  );

  const handleRattachement = useCallback(
    async (platform) => {
      if (!userId) return;
      const response = await api.post('/services/attach', { userId, platform });
      if (response?.ok) {
        onAttachSuccess?.();
      }
    },
    [userId, onAttachSuccess]
  );

  return (
    <Dialog open={open} onClose={onClose} title="APPLIS">
      <Box sx={{ marginBottom: '30px' }}>
        {SERVICE_OPTIONS.map(({ key, label, icon: Icon }) => {
          const attachedService = services.find((s) => s.platform === key);

          return (
            <ServiceRow key={key}>
              <ServiceLogo>{Icon && <Icon />}</ServiceLogo>
              <ServiceName>{label}</ServiceName>
              <ServiceActions>
                {attachedService ? (
                  <>
                    <TrashButton
                      onClick={() => handleRemove(attachedService.id)}
                      aria-label={`Supprimer ${label}`}
                    >
                      <TrashIcon />
                    </TrashButton>
                    <StatusBadge
                      active
                      variant={attachedService.status}
                      sx={{ cursor: 'pointer' }}
                      onClick={(e) => handleStatusClick(e, attachedService)}
                    />
                  </>
                ) : (
                  <RattachementText onClick={() => handleRattachement(key)}>
                    RATTACHEMENT
                  </RattachementText>
                )}
              </ServiceActions>
            </ServiceRow>
          );
        })}
      </Box>
      <DialogPrimaryButton variant="contained" onClick={handleRetrait}>
        ENREGISTRER
      </DialogPrimaryButton>

      {!!statusService && (
        <StatusMenu
          anchorEl={statusAnchorEl}
          open={statusMenuOpen}
          onClose={(_, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') handleStatusMenuClose();
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {[SERVICE_STATUS_VARIANTS.pending, SERVICE_STATUS_VARIANTS.verified]
            .filter((status) => status !== statusService.status)
            .map((status) => (
              <StatusMenuItem key={status} onClick={() => handleStatusSelect(status)} disableRipple>
                <StatusBadge variant={status} />
              </StatusMenuItem>
            ))}
        </StatusMenu>
      )}
    </Dialog>
  );
};

export default ApplicationsDialog;
